import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Drawer, Select, Space, Switch, Typography } from 'antd';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { QuestionSetItemsField } from '@/components/QuestionSetItemsField';
import { QuestionForm, type QuestionFormValues } from '@/components/QuestionForm';
import { DrawerHeader } from '@/components/DrawerHeader';
import { EditableTitle } from '@/components/EditableTitle';
import { FullScreenSpinner } from '@/components/FullScreenSpinner';
import { BuilderChip } from '@/components/BuilderChip';
import { usePageBackLink, useDrawerSurface } from '@/hooks';
import {
  AdminQuestionSetsService,
  AdminHtsQuestionnairesService,
  BaseAPIClient,
  QuestionsService,
} from '@/services/api';
import type {
  HtsQuestionnaire,
  QuestionSetWithPackages,
  QuestionWithQuestionSets,
} from '@/services/api';
import { containerVariants, itemVariants } from '@/utils';
import { ROUTES, buildRoute } from '@/routes';
import styles from './AdminQuestionSets.module.scss';

interface QuestionSetEditorProps {
  /** Absent for the "new" route. */
  id?: string;
}

export const QuestionSetEditor: React.FC<QuestionSetEditorProps> = ({ id }) => {
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();
  const navigate = useNavigate();
  const drawerSurface = useDrawerSurface();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState<QuestionWithQuestionSets[]>([]);
  const [existing, setExisting] = useState<QuestionSetWithPackages | null>(null);
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [htsEnabled, setHtsEnabled] = useState(false);
  const [htsQuestionnaireId, setHtsQuestionnaireId] = useState<string | null>(null);
  const [questionnaires, setQuestionnaires] = useState<HtsQuestionnaire[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<QuestionWithQuestionSets | null>(null);

  usePageBackLink(() => navigate(ROUTES.ADMIN_QUESTION_SETS), 'Back to Question Sets');

  const getServices = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return {
      questionSets: new AdminQuestionSetsService(token),
      questions: new QuestionsService(token),
      htsQuestionnaires: new AdminHtsQuestionnairesService(token),
    };
  }, [getToken]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const services = await getServices();
      const [questionList, questionnaireList, current] = await Promise.all([
        services.questions.list(),
        services.htsQuestionnaires.list(),
        id ? services.questionSets.getById(id) : Promise.resolve(null),
      ]);
      setQuestions(questionList);
      setQuestionnaires(questionnaireList);
      setExisting(current);
      setName(current?.name ?? '');
      setSelectedIds(current ? current.items.map((item) => item.questionId) : []);
      const initialHtsId = current?.htsQuestionnaireId ?? null;
      setHtsQuestionnaireId(initialHtsId);
      setHtsEnabled(initialHtsId !== null);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getServices, id, notification]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const toggleHts = (checked: boolean) => {
    setHtsEnabled(checked);
    if (checked && !htsQuestionnaireId) {
      setHtsQuestionnaireId(questionnaires[0]?.id ?? null);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      notification.error({ message: 'Name is required' });
      return;
    }
    if (selectedIds.length === 0) {
      notification.error({ message: 'At least one question is required' });
      return;
    }
    if (htsEnabled && !htsQuestionnaireId) {
      notification.error({ message: 'Select an HTS questionnaire, or turn HTS Required off' });
      return;
    }

    setSaving(true);
    try {
      const services = await getServices();
      const items = selectedIds.map((questionId, order) => ({ questionId, order }));
      const nextHtsQuestionnaireId = htsEnabled ? htsQuestionnaireId : null;
      if (existing) {
        await services.questionSets.update(existing.id, {
          name: name.trim(),
          htsQuestionnaireId: nextHtsQuestionnaireId,
          items,
        });
      } else {
        await services.questionSets.create({
          name: name.trim(),
          htsQuestionnaireId: nextHtsQuestionnaireId,
          items,
        });
      }
      notification.success({ message: 'Question set saved' });
      navigate(ROUTES.ADMIN_QUESTION_SETS);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionSubmit = async (values: QuestionFormValues) => {
    if (!editingQuestion) return;
    try {
      const services = await getServices();
      await services.questions.update(editingQuestion.id, values);
      notification.success({ message: 'Question updated' });
      setEditingQuestion(null);
      setQuestions(await services.questions.list());
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    try {
      const services = await getServices();
      await services.questionSets.remove(existing.id);
      notification.success({ message: 'Question set deleted' });
      navigate(ROUTES.ADMIN_QUESTION_SETS);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const goToPackage = (packageId: string) =>
    navigate(buildRoute(ROUTES.ADMIN_PACKAGE_EDIT, { id: packageId }));

  const confirmDelete = () => {
    if (!existing) return;
    const inUse = existing.packages.length > 0;
    const instance = modal.confirm({
      title: 'Delete this question set?',
      icon: null,
      maskClosable: true,
      centered: true,
      content: inUse ? (
        <div className={styles.deleteHint}>
          <div>Unassign it from these packages before it can be deleted:</div>
          <div className={styles.packageRefs}>
            {existing.packages.map((pkg) => (
              <BuilderChip
                key={pkg.id}
                tint="purple"
                onClick={() => {
                  instance.destroy();
                  goToPackage(pkg.id);
                }}
              >
                {pkg.name}
              </BuilderChip>
            ))}
          </div>
        </div>
      ) : (
        "This cannot be undone."
      ),
      okText: 'Delete',
      okButtonProps: { danger: true, disabled: inUse },
      onOk: () => handleDelete(),
    });
  };

  if (loading) return <FullScreenSpinner />;

  const canSave = Boolean(name.trim()) && selectedIds.length > 0 && (!htsEnabled || Boolean(htsQuestionnaireId));

  return (
    <motion.div className={styles.editorPage} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div variants={itemVariants}>
      <div className={styles.editorHead}>
        <div className={styles.nameRow}>
          <EditableTitle value={name} onChange={setName} placeholder="Question set name…" textClassName={styles.nameInput} />
        </div>
        <Space>
          {existing && (
            <Button className={styles.btnSecondaryDanger} onClick={confirmDelete}>
              Delete
            </Button>
          )}
          <Button
            className={styles.btnPrimary}
            onClick={() => save()}
            loading={saving}
            disabled={!canSave}
          >
            {existing ? 'Save' : 'Create question set'}
          </Button>
        </Space>
      </div>
      </motion.div>

      <motion.div variants={itemVariants}>
      <div className={styles.htsSection}>
        <div className={styles.htsToggleRow}>
          <div>
            <Typography.Text strong className={styles.htsToggleLabel}>HTS Required</Typography.Text>
            <Typography.Text className={styles.htsToggleHint}>
              Candidates must complete a High-Trust Suitability questionnaire before this interview.
            </Typography.Text>
          </div>
          <Switch
            checked={htsEnabled}
            onChange={toggleHts}
            disabled={questionnaires.length === 0 && !htsEnabled}
          />
        </div>

        {htsEnabled && (
          <div className={styles.htsPickerRow}>
            <Typography.Text className={styles.htsPickerLabel}>Questionnaire</Typography.Text>
            <Select
              value={htsQuestionnaireId}
              onChange={setHtsQuestionnaireId}
              popupMatchSelectWidth={false}
              options={questionnaires.map((q) => ({ label: q.name, value: q.id }))}
              className={styles.htsQuestionnaireSelect}
            />
          </div>
        )}

        {questionnaires.length === 0 && (
          <Typography.Text className={styles.htsEmptyHint}>
            No HTS questionnaires exist yet —{' '}
            <a onClick={() => navigate(ROUTES.ADMIN_HTS_QUESTIONNAIRE_NEW)}>create one first</a>.
          </Typography.Text>
        )}
      </div>

      <QuestionSetItemsField
        questions={questions}
        selectedIds={selectedIds}
        onChange={setSelectedIds}
        onEditQuestion={setEditingQuestion}
        referencingPackages={existing?.packages}
      />
      </motion.div>

      <Drawer
        title={<DrawerHeader title="Edit Question" onClose={() => setEditingQuestion(null)} />}
        closable={false}
        open={Boolean(editingQuestion)}
        onClose={() => setEditingQuestion(null)}
        {...drawerSurface}
        destroyOnHidden
        className={styles.drawer}
      >
        {editingQuestion && (
          <QuestionForm
            initialValues={editingQuestion}
            affectedQuestionSets={editingQuestion.questionSets}
            meta={editingQuestion}
            onSubmit={handleQuestionSubmit}
          />
        )}
      </Drawer>
    </motion.div>
  );
};

export default QuestionSetEditor;
